import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { withStyles } from '@material-ui/core/styles';
import withMobileDialog from '@material-ui/core/withMobileDialog';

import InvoiceCreditMemoContainer from '../../containers/accounting/InvoiceCreditMemo';
import FMPText from '../pureForm/FMPText';
import StationSelect from '../form/StationSelect';
import Note from '../ui/Note';
import GenericSavingDialog from './GenericSavingDialog';
import Transition from './Transition';
import { moneyFormatter } from '../../pages/accounting/general';

const styles = (theme) => ({
	dialogPadding: {
		padding: 10,
		[theme.breakpoints.up('md')]: {
			padding: '20px 50px 20px 50px',
		},
	},
	dialogPaper: {
		overflow: 'scroll',
		maxWidth: '100%',
		width: 700,
		maxHeight: 'calc(100% - 20px)',
		margin: 10,
	},
	optionDescription: {
		color: '#888',
		fontSize: '85%',
		marginLeft: 32,
		marginTop: -6,
		marginBottom: 8,
	},
});

//- Credit & Rebill: fully credits an issued invoice, then (optionally) re-issues it.
//- Creating here only makes PENDING records -- nothing emails or hits QuickBooks until
//- the send step (the rebill invoice's "Send Credit & Rebill", or "Send Credit Memo"
//- here for credit-only).
class CreditRebillDialog extends Component {
	constructor(props) {
		super(props);
		this.state = this.getInitialState();
	}

	getInitialState = () => ({
		rebillType: 'rebill',
		reason: '',
		stationId: undefined,
		formFieldError: {},
		isSavingOpen: false,
		savingStatus: undefined,
		createdCreditMemo: undefined,
	});

	componentDidUpdate(prevProps) {
		if (!prevProps.open && this.props.open) {
			this.setState(this.getInitialState());
		}
	}

	//- controller pattern used by StationSelect / FMPText
	onChange = (value, path) => {
		const arrayPath = _.isString(path) ? _.toPath(path) : path;
		const val = _.isObject(value) && value.target ? value.target.value : value;
		this.setState({ [_.head(arrayPath)]: val });
	};
	getValue = (path, defaultValue) => {
		return _.get(this.state, path, _.isUndefined(defaultValue) ? '' : defaultValue);
	};
	getError = (path) => {
		return _.get(this.state.formFieldError, path);
	};

	handleCreate = () => {
		const { stationInvoice, createInvoiceCreditMemo, showErrors } = this.props;
		const { rebillType, reason, stationId } = this.state;

		const errors = [];
		const formFieldError = {};
		if (!reason) {
			errors.push('A reason is required');
			_.set(formFieldError, ['reason'], 'Reason is required');
		}
		if (rebillType === 'newBill' && !stationId) {
			errors.push('Pick the station that should receive the new bill');
			_.set(formFieldError, ['stationId'], 'Station is required');
		}
		if (errors.length) {
			this.setState({ formFieldError });
			return showErrors(errors);
		}

		this.setState({ isSavingOpen: true, savingStatus: 'activating' });

		const params = {
			stationInvoiceId: stationInvoice.stationInvoiceId,
			reason,
			rebillType,
		};
		if (rebillType === 'newBill') {
			params.newStationId = stationId;
		}

		createInvoiceCreditMemo({
			params,
			callback: (err, r) => {
				if (err) {
					this.setState({ isSavingOpen: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}

				const creditMemo = _.get(r, ['collections', 'invoiceCreditMemoCollection', 0]);
				const rebillInvoiceId = r.rebillInvoiceId;

				if (rebillType === 'creditOnly') {
					this.setState({
						isSavingOpen: false,
						createdCreditMemo: creditMemo,
					});
					return;
				}

				//- Edit the pending rebill next; it sends with "Send Credit & Rebill".
				//- Full navigation so the invoice page mounts fresh on the new id.
				window.location.href = `/accounting/stationInvoice/update/${rebillInvoiceId}`;
			},
		});
	};

	handleSendCreditOnly = () => {
		const { sendInvoiceCreditMemo, showErrors } = this.props;
		const { createdCreditMemo } = this.state;

		this.setState({ isSavingOpen: true, savingStatus: 'activating' });
		sendInvoiceCreditMemo({
			params: { invoiceCreditMemoId: createdCreditMemo.invoiceCreditMemoId },
			callback: (err) => {
				if (err) {
					this.setState({ isSavingOpen: false });
					return showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ savingStatus: 'done' });
			},
		});
	};

	closeSavingDialog = () => {
		this.setState({ isSavingOpen: false });
	};

	continueSavingDialog = () => {
		window.location.reload();
	};

	render() {
		const { classes, fullScreen, open, onClose, stationInvoice, stations } = this.props;
		const { rebillType, isSavingOpen, savingStatus, createdCreditMemo } = this.state;

		if (!stationInvoice) {
			return null;
		}

		const { stationInvoiceId, total } = stationInvoice;

		return (
			<Dialog
				fullScreen={fullScreen}
				maxWidth="md"
				open={open}
				TransitionComponent={Transition}
				onClose={onClose}
				classes={{ paper: classes.dialogPaper }}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>Credit &amp; Rebill Invoice #{stationInvoiceId}</DialogTitle>
					<DialogContent>
						{!createdCreditMemo && (
							<div>
								<Note>
									This credits the <strong>full amount of {moneyFormatter.format(total)}</strong>{' '}
									back to the customer. The credit memo is emailed, created in QuickBooks, and
									applied against the original invoice. The original invoice becomes
									&quot;Credited&quot;.
								</Note>

								<RadioGroup
									style={{ marginTop: 15 }}
									value={rebillType}
									onChange={(e) => this.setState({ rebillType: e.target.value })}
								>
									<FormControlLabel
										value="rebill"
										control={<Radio color="primary" />}
										label="Rebill the same customer"
									/>
									<div className={classes.optionDescription}>
										Creates a pending copy of this invoice to fix and send. The customer gets one
										email with the credit memo and the corrected invoice.
									</div>

									<FormControlLabel
										value="newBill"
										control={<Radio color="primary" />}
										label="Bill a different station"
									/>
									<div className={classes.optionDescription}>
										Fuel went to the wrong place: this customer gets the credit memo, and a pending
										invoice is created for the station below to review and send.
									</div>

									<FormControlLabel
										value="creditOnly"
										control={<Radio color="primary" />}
										label="Credit only (no rebill)"
									/>
									<div className={classes.optionDescription}>
										Just the credit memo. Nothing is re-invoiced.
									</div>
								</RadioGroup>

								{rebillType === 'newBill' && (
									<div style={{ marginTop: 10 }}>
										<StationSelect
											label="Station to bill"
											path={['stationId']}
											controller={this}
											stations={stations || []}
										/>
									</div>
								)}

								<FMPText
									label="Reason (required, kept on the credit memo record)"
									path="reason"
									value={this.getValue('reason')}
									error={this.getError('reason')}
									multiline={2}
									onChange={this.onChange}
								/>
							</div>
						)}

						{!!createdCreditMemo && (
							<div>
								<Note>
									Credit memo <strong>#{createdCreditMemo.invoiceCreditMemoId}</strong> created
									(pending). Send it now to email the customer, create it in QuickBooks, and apply
									it against invoice #{stationInvoiceId} — or send it later from the Credits &amp;
									Rebills page.
								</Note>
							</div>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={onClose}>{createdCreditMemo ? 'Send Later' : 'Cancel'}</Button>
						{!createdCreditMemo && (
							<Button
								variant="contained"
								color="primary"
								disabled={isSavingOpen}
								onClick={this.handleCreate}
							>
								{rebillType === 'creditOnly'
									? 'Create Credit Memo'
									: 'Create Credit Memo & Pending Rebill'}
							</Button>
						)}
						{!!createdCreditMemo && (
							<Button
								variant="contained"
								color="primary"
								disabled={isSavingOpen}
								onClick={this.handleSendCreditOnly}
							>
								Send Credit Memo Now
							</Button>
						)}
					</DialogActions>
				</div>

				<GenericSavingDialog
					open={isSavingOpen}
					onClose={this.closeSavingDialog}
					onContinue={this.continueSavingDialog}
					processingText={
						createdCreditMemo
							? 'Emailing credit memo, creating in QuickBooks, applying to the original invoice...'
							: 'Creating credit memo...'
					}
					successText={
						createdCreditMemo
							? 'Credit memo sent, created in QuickBooks, and applied.'
							: 'Created.'
					}
					status={savingStatus}
				/>
			</Dialog>
		);
	}
}

CreditRebillDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	fullScreen: PropTypes.bool.isRequired,
	open: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	stationInvoice: PropTypes.object,
	stations: PropTypes.array,

	//- container
	createInvoiceCreditMemo: PropTypes.func.isRequired,
	sendInvoiceCreditMemo: PropTypes.func.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([
	withStyles(styles),
	withMobileDialog(),
	InvoiceCreditMemoContainer,
])(CreditRebillDialog);
